/**
 * DeadlineCard Component
 *
 * Displays a single deadline as a card with priority colors, status badge,
 * days remaining, and quick actions.
 */

import { Calendar, CalendarPlus, Check, X, Edit2, RotateCcw, Sparkles, Clock } from 'lucide-react';
import {
  DeadlineSummary,
  PRIORITY_STYLES,
  STATUS_STYLES,
  TYPE_COLORS,
  DEADLINE_TYPES,
  formatDaysRemaining,
  formatShortDate,
} from './types';

interface DeadlineCardProps {
  deadline: DeadlineSummary;
  onClick?: () => void;
  onComplete?: (deadlineId: string) => void;
  onDismiss?: (deadlineId: string) => void;
  onEdit?: (deadlineId: string) => void;
  onExport?: (deadlineId: string) => void;
  showCaseFile?: boolean;
}

export function DeadlineCard({
  deadline,
  onClick,
  onComplete,
  onDismiss,
  onEdit,
  onExport,
  showCaseFile = false,
}: DeadlineCardProps): JSX.Element {
  const priorityStyle = PRIORITY_STYLES[deadline.priority];
  const statusStyle = STATUS_STYLES[deadline.status];
  const typeColor = TYPE_COLORS[deadline.type];
  const typeLabel = DEADLINE_TYPES.find((t) => t.value === deadline.type)?.label || deadline.type;

  const isActionable = deadline.status !== 'completed' && deadline.status !== 'dismissed';

  // Determine urgency indicator color
  const getUrgencyColor = () => {
    if (deadline.status === 'overdue') return 'text-red-600';
    if (deadline.status === 'due_soon') return 'text-orange-600';
    return 'text-muted';
  };

  return (
    <div
      className={`card group hover:shadow-card-hover transition-all border-l-4 ${priorityStyle.border} ${
        onClick ? 'cursor-pointer' : ''
      }`}
      onClick={onClick}
    >
      <div className="card-body">
        {/* Header with type, status, and recurring indicator */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {/* Type badge */}
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${typeColor}`}>
              {typeLabel}
            </span>
            {/* Priority badge — text label so priority is not conveyed by the
                colored left border alone (WCAG 1.4.1). Mirrors CareLogCard's
                severity badge; 'low' is the baseline and left unlabelled. */}
            {deadline.priority !== 'low' && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${priorityStyle.bg} ${priorityStyle.text}`}>
                {deadline.priority}
              </span>
            )}
            {/* Recurring indicator */}
            {deadline.isRecurring && (
              <span className="flex items-center gap-1 text-xs text-muted" title="Recurring deadline">
                <RotateCcw size={12} />
              </span>
            )}
            {/* AI-extracted indicator */}
            {deadline.creationSource === 'ai_extracted' && (
              <span className="flex items-center gap-1 text-xs text-purple-600" title="AI-extracted from document">
                <Sparkles size={12} />
              </span>
            )}
          </div>
          {/* Status badge */}
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
            {deadline.status.replace('_', ' ')}
          </span>
        </div>

        {/* Title */}
        <h3 className="font-semibold text-ink mb-2">{deadline.title}</h3>

        {/* Due date and days remaining */}
        <div className="flex items-center gap-4 text-sm mb-2">
          <span className="flex items-center gap-1 text-muted">
            <Calendar size={14} />
            {formatShortDate(deadline.dueDate)}
          </span>
          <span className={`flex items-center gap-1 font-medium ${getUrgencyColor()}`}>
            <Clock size={14} />
            {formatDaysRemaining(deadline.dueDate)}
          </span>
        </div>

        {/* CaseFile name (for dashboard widget) */}
        {showCaseFile && deadline.caseFileName && (
          <p className="text-xs text-muted mb-2">
            CaseFile: {deadline.caseFileName}
          </p>
        )}

        {/* Quick actions - visible on hover */}
        {isActionable && (onComplete || onDismiss || onEdit || onExport) && (
          <div className="pt-2 border-t border-soft-neutral opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
            {onComplete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onComplete(deadline.deadlineId);
                }}
                className="btn-ghost btn-sm flex items-center gap-1 text-green-600 hover:bg-green-50"
                title="Mark as completed"
              >
                <Check size={14} />
                Complete
              </button>
            )}
            {onDismiss && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDismiss(deadline.deadlineId);
                }}
                className="btn-ghost btn-sm flex items-center gap-1 text-gray-500 hover:bg-gray-100"
                title="Dismiss"
              >
                <X size={14} />
                Dismiss
              </button>
            )}
            {onEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(deadline.deadlineId);
                }}
                className="btn-ghost btn-sm flex items-center gap-1"
                title="Edit"
              >
                <Edit2 size={14} />
                Edit
              </button>
            )}
            {onExport && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onExport(deadline.deadlineId);
                }}
                className="btn-ghost btn-sm flex items-center gap-1"
                title="Export to calendar"
                data-testid="card-export-button"
              >
                <CalendarPlus size={14} />
                Export
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default DeadlineCard;
